import { useEffect, useState } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import axios from "axios";
import { Loader2, Send, Sparkles, Upload, X } from "lucide-react";
import { toast } from "sonner";
import QuickActionGrid from "../../components/grapho/QuickActionGrid";
import {
  API,
  NICHE_OPTIONS,
  PREFILL_NOTE,
  STYLE_PRESETS,
  THEME_OPTIONS,
  getStylePrefs,
  inputCls,
} from "./shared";

export default function NewBriefPage() {
  const { customer, briefs, refresh } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [request, setRequest] = useState("");
  const [designCount, setDesignCount] = useState(1);
  const [style, setStyle] = useState("");
  const [niche, setNiche] = useState("");
  const [theme, setTheme] = useState("");
  const [extra, setExtra] = useState("");
  const [uploads, setUploads] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Prefill from revision shortcuts ("create a variation of...") and creative preferences.
  useEffect(() => {
    const st = location.state || {};
    if (st.prefill) setRequest(st.prefill);
    if (st.count) setDesignCount(Math.max(1, Math.min(10, st.count)));
    if (st.style) setStyle(st.style);
    if (st.niche) setNiche(st.niche);
    if (st.theme) setTheme(st.theme);
    else {
      const prefs = getStylePrefs();
      if (prefs[0]) setStyle(prefs[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pastStyles = [...new Set(briefs.map((b) => (b.style || "").split(",").map((x) => x.trim())).flat().filter(Boolean))];

  const designCost = Math.max(1, uploads.length, designCount);
  const creditsAfter = Math.max(0, customer.credits_remaining - designCost);

  const onFiles = (e) => {
    const picked = Array.from(e.target.files || [])
      .filter((f) => f.type === "image/png")
      .slice(0, 10);
    if (picked.length < (e.target.files || []).length) {
      toast.error("Only PNG images are accepted — up to 10 per submission.");
    }
    setUploads(picked.map((f) => ({ file: f, note: "" })));
  };

  const setNote = (i, v) => setUploads((u) => u.map((row, idx) => (idx === i ? { ...row, note: v } : row)));

  const submit = async (e) => {
    e.preventDefault();
    if (request.trim().length < 5) {
      toast.error("Tell us what to design — even a rough idea works.");
      return;
    }
    setSubmitting(true);
    try {
      const fullRequest = extra.trim() ? `${request.trim()}\n\nAdditional instructions: ${extra.trim()}` : request.trim();
      const fd = new FormData();
      fd.append("email", customer.email);
      fd.append("request", fullRequest);
      fd.append("style", style.trim());
      fd.append("niche", niche);
      fd.append("theme", theme);
      fd.append("design_count", designCost);
      fd.append("notes", JSON.stringify(uploads.map((u) => u.note)));
      uploads.forEach((u) => fd.append("files", u.file));
      await axios.post(`${API}/briefs/submit`, fd);
      toast.success(`Brief received — ${designCost} credit${designCost > 1 ? "s" : ""} used. The studio is on it.`);
      await refresh();
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed — please try again.");
    }
    setSubmitting(false);
  };

  if (!customer) return null;

  return (
    <div data-testid="new-brief-page">
      <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-sky-400">Grapho Studio</p>
      <h1 className="mt-2 text-2xl font-extrabold tracking-tight text-white sm:text-3xl">New Brief</h1>
      <p className="mt-1 text-sm text-slate-400">Four quick steps. Even a rough idea works.</p>

      <form onSubmit={submit} noValidate data-testid="new-brief-form" className="mt-8 space-y-5">
        {/* Step 1 */}
        <section className="rounded-2xl border border-amber-400/25 bg-[#0C1222]/90 p-5">
          <p className="font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-amber-300">
            Step 1 — What do you want?
          </p>
          <div className="mt-4">
            <QuickActionGrid
              prefix="new-brief"
              onPrefill={(text) => setRequest(text)}
            />
          </div>
          <textarea
            data-testid="brief-request-input"
            rows={3}
            value={request}
            onChange={(e) => setRequest(e.target.value)}
            placeholder="Tell us what you're thinking — even if it's rough."
            className={`${inputCls} mt-3 resize-none`}
          />
          <label className="mt-4 block text-xs font-semibold uppercase tracking-wider text-slate-400">
            How many designs?
            <select
              data-testid="brief-design-count-select"
              value={designCount}
              onChange={(e) => setDesignCount(Number(e.target.value))}
              className={`${inputCls} mt-2`}
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <option key={n} value={n}>
                  {n} design{n > 1 ? "s" : ""} — {n} credit{n > 1 ? "s" : ""}
                </option>
              ))}
            </select>
          </label>
        </section>

        {/* Step 2 */}
        <section className="rounded-2xl border border-white/10 bg-[#0C1222]/80 p-5">
          <p className="font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-amber-300">
            Step 2 — Upload references
          </p>
          <label
            htmlFor="brief-upload"
            data-testid="brief-upload-input"
            className="mt-4 flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-white/15 bg-[#070B14]/60 px-4 py-4 text-sm text-slate-400 transition-colors hover:border-amber-400/40 hover:text-slate-200"
          >
            <Upload className="h-4 w-4 shrink-0 text-amber-300" />
            {uploads.length
              ? `${uploads.length} image${uploads.length > 1 ? "s" : ""} selected`
              : "Upload up to 10 reference PNG images (optional)"}
          </label>
          <input
            id="brief-upload"
            data-testid="brief-file-input"
            type="file"
            accept="image/png"
            multiple
            className="hidden"
            onChange={onFiles}
          />
          {uploads.map((u, i) => (
            <div key={i} className="mt-2 flex flex-wrap items-center gap-2 rounded-xl border border-white/10 bg-[#070B14]/60 p-3">
              <span className="max-w-[180px] truncate text-xs text-slate-300">{u.file.name}</span>
              <input
                data-testid={`brief-note-input-${i}`}
                type="text"
                value={u.note}
                onChange={(e) => setNote(i, e.target.value)}
                placeholder="Exact changes needed for this design..."
                className="min-w-0 flex-1 rounded-lg border border-white/10 bg-[#070B14]/80 px-3 py-2 text-xs text-slate-100 placeholder:text-slate-600 focus:border-sky-400/60 focus:outline-none"
              />
              <button
                type="button"
                aria-label={`Remove ${u.file.name}`}
                data-testid={`brief-remove-file-${i}`}
                onClick={() => setUploads((up) => up.filter((_, idx) => idx !== i))}
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-white/10 text-slate-500 hover:border-rose-400/40 hover:text-rose-300"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <p className="mt-2 text-xs leading-snug text-slate-500">{PREFILL_NOTE}</p>
        </section>

        {/* Step 3 */}
        <section className="rounded-2xl border border-white/10 bg-[#0C1222]/80 p-5">
          <p className="font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-amber-300">
            Step 3 — Choose niche and style
          </p>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400">
              Niche
              <select data-testid="brief-niche-select" value={niche} onChange={(e) => setNiche(e.target.value)} className={`${inputCls} mt-2`}>
                <option value="">Select a niche (optional)</option>
                {NICHE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400">
              Seasonal theme
              <select data-testid="brief-theme-select" value={theme} onChange={(e) => setTheme(e.target.value)} className={`${inputCls} mt-2`}>
                <option value="">Select a theme (optional)</option>
                {THEME_OPTIONS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </label>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400">
              Style
              <input
                data-testid="brief-style-input"
                type="text"
                list="style-presets"
                value={style}
                onChange={(e) => setStyle(e.target.value)}
                placeholder="e.g. Bold Retro"
                className={`${inputCls} mt-2`}
              />
              <datalist id="style-presets">
                {[...new Set([...pastStyles, ...STYLE_PRESETS])].map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            </label>
          </div>
        </section>

        {/* Step 4 */}
        <section className="rounded-2xl border border-white/10 bg-[#0C1222]/80 p-5">
          <p className="font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-amber-300">
            Step 4 — Additional instructions
          </p>
          <textarea
            data-testid="brief-extra-input"
            rows={2}
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            placeholder="Anything else the designers should know (optional)."
            className={`${inputCls} mt-4 resize-none`}
          />
        </section>

        <div
          data-testid="brief-credit-preview"
          className="rounded-xl border border-sky-400/25 bg-sky-400/5 px-4 py-3 text-xs font-semibold text-sky-200"
        >
          {designCost} design{designCost > 1 ? "s" : ""} requested — {designCost} credit{designCost > 1 ? "s" : ""} —{" "}
          {creditsAfter} credit{creditsAfter === 1 ? "" : "s"} remaining after submission.
          {creditsAfter === 0 && customer.credits_remaining < designCost && (
            <span className="mt-1 block text-rose-300">
              Not enough credits for this order — upgrade your plan to keep creating.
            </span>
          )}
        </div>

        <button
          data-testid="brief-submit-button"
          type="submit"
          disabled={submitting}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-amber-400 px-6 py-3.5 text-sm font-bold text-[#1A1204] transition-all duration-300 hover:bg-amber-300 hover:shadow-[0_0_30px_rgba(245,158,11,0.45)] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {submitting ? "Sending to the studio..." : `Submit Brief — ${designCost} Credit${designCost > 1 ? "s" : ""}`}
        </button>
        <p className="text-center text-xs text-slate-600">Your brief will enter production after submission.</p>
      </form>
    </div>
  );
}
